import React, {useRef, useState} from "react";
import {DateRange, ArrowDropDown} from "@mui/icons-material";
import {
    Box,
    Button,
    Popover,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {useWatch} from "react-hook-form";
import DateRangeCalendar from '../utils/DateRangeCalendar';
import {formatDateRange} from '../utils/dateRangeUtils';
import moment from "moment-timezone";
import {dateRangePopoverStyles, dateRangeFooterStyles, DateRangePickerHeader, DateRangeSelection} from '../utils/DateRangePickerDesign';

function DateTimeRangePicker(
    {
        control,
        setValue,
        fromName = "metrics.runWindow.dateTimeRange.from",
        toName = "metrics.runWindow.dateTimeRange.to"
    }
) {
    const theme = useTheme();
    const down768 = useMediaQuery(theme.breakpoints.down(768));
    // Values are stored directly in React Hook Form
    const from = useWatch({control, name: fromName});
    const to = useWatch({control, name: toName});

    // Temporary values while the popup is open
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const [startTime, setStartTime] = useState("00:00");
    const [endTime, setEndTime] = useState("23:59");

    const [focusedInput, setFocusedInput] = useState("startDate");
    const [anchorEl, setAnchorEl] = useState(null);
    const popoverActions = useRef(null);


    const handleOpen = (event) => {
        /*
         * Copy existing form values into temporary picker state.
         *
         * This means opening an already configured rule will show
         * the currently selected values.
         */
        if (from) {
            const fromMoment = moment(from);

            setStartDate(fromMoment);
            setStartTime(fromMoment.format("HH:mm"));
        } else {
            setStartDate(null);
            setStartTime("00:00");
        }

        if (to) {
            const toMoment = moment(to);

            setEndDate(toMoment);
            setEndTime(toMoment.format("HH:mm"));
        } else {
            setEndDate(null);
            setEndTime("23:59");
        }

        setFocusedInput("startDate");
        setAnchorEl(event.currentTarget);
    };


    const handleClose = () => {
        setAnchorEl(null);
    };


    const handleApply = () => {
        const range = formatDateRange(startDate, endDate, startTime, endTime, false);
        if (!range) return;

        // Store values directly in useForm()
        setValue(
            fromName,
            range.from,
            {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
            }
        );

        setValue(
            toName,
            range.to,
            {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
            }
        );

        handleClose();
    };


    const hasInvalidRange = !!(startDate && endDate && !formatDateRange(startDate, endDate, startTime, endTime, false));

    // Display value on the form
    const dateLabel =
        from && to
            ? `${moment(from).format("D MMM YYYY HH:mm")} - ${moment(to).format("D MMM YYYY HH:mm")}`
            : "Select date and time";


    return (
        <Box
            sx={{
                flexShrink: 0,
                minWidth: 0,
                color: "text.secondary",
            }}
        >
            <Button
                id="automation-date-time-range"
                onClick={handleOpen}
                startIcon={<DateRange/>}
                endIcon={<ArrowDropDown/>}
                color="inherit"
                variant="outlined"
                size="medium"
                sx={{
                    textTransform: "none",
                    whiteSpace: "normal",
                    maxWidth: '100%',
                    textAlign: 'left',
                    borderRadius: '8px',
                    borderColor: 'divider',
                    color: "text.primary",
                }}
            >
                {dateLabel}
            </Button>


            <Popover
                id="date-time-range-picker"
                action={popoverActions}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                marginThreshold={8}
                slotProps={{transition: {onEntered: () => popoverActions.current?.updatePosition()}, paper: {sx: dateRangePopoverStyles}}}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
            >
                <Box sx={{width: down768 ? 304 : 632, maxWidth: '100%'}}>
                    <DateRangePickerHeader title="Date and time" description="Set your automation window" onClose={handleClose}/>
                    <Box sx={{px: {xs: 2, sm: 2.5}, display: 'grid', gap: 2.5, pb: 2.5}}>
                        <DateRangeSelection startDate={startDate} endDate={endDate} focusedInput={focusedInput} onFocusChange={setFocusedInput}/>
                        <DateRangeCalendar
                            minDate={moment()}
                            numberOfMonths={down768 ? 1 : 2}
                            daySize={down768 ? 38 : 40}
                            startDate={startDate}
                            endDate={endDate}
                            onDatesChange={({
                                                startDate,
                                                endDate,
                                            }) => {
                                setStartDate(startDate);
                                setEndDate(endDate);
                            }}
                            focusedInput={focusedInput}
                            onFocusChange={setFocusedInput}
                            orientation="horizontal"
                        />
                    </Box>
                    {/* TIME SELECTION */}
                    <Box
                        sx={{
                            ...dateRangeFooterStyles,
                            display: "grid",
                            gridTemplateColumns: down768 ? 'repeat(2, minmax(0, 1fr))' : '140px 140px minmax(0, 1fr)',
                            alignItems: "flex-start",
                        }}
                    >
                        <TextField
                            type="time"
                            label="Start time"
                            size="small"
                            value={startTime}
                            onChange={(event) =>
                                setStartTime(
                                    event.target.value
                                )
                            }
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                                htmlInput: {
                                    step: 60,
                                },
                            }}
                            sx={{
                                width: '100%', minWidth: 0,

                                "& input[type='time']": {colorScheme: theme => theme.palette.mode},
                            }}
                        />


                        <TextField
                            type="time"
                            label="End time"
                            size="small"
                            value={endTime}
                            onChange={(event) =>
                                setEndTime(
                                    event.target.value
                                )
                            }
                            slotProps={{
                                inputLabel: {
                                    shrink: true,
                                },
                                htmlInput: {
                                    step: 60,
                                },
                            }}
                            sx={{
                                width: '100%', minWidth: 0,

                                "& input[type='time']": {colorScheme: theme => theme.palette.mode},
                            }}
                        />


                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                gap: 1,
                                gridColumn: down768 ? '1 / -1' : '3',
                            }}
                        >
                            <Button
                                color="inherit"
                                onClick={handleClose}
                                sx={{
                                    textTransform: "none",
                                }}
                            >
                                Cancel
                            </Button>

                            <Button
                                variant="contained"
                                disabled={
                                    !startDate ||
                                    !endDate ||
                                    hasInvalidRange
                                }
                                onClick={handleApply}
                                sx={{
                                    textTransform: "none",
                                }}
                            >
                                Apply
                            </Button>
                        </Box>
                    </Box>


                    {hasInvalidRange && (
                        <Typography
                            color="error"
                            variant="caption"
                            sx={{
                                display: "block",
                                px: 2,
                                pb: 1,
                            }}
                        >
                            End date and time must be after the start date and time.
                        </Typography>
                    )}
                </Box>
            </Popover>
        </Box>
    );
}

export default DateTimeRangePicker;
